import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Typography from '@mui/joy/Typography'
import { useTranslation } from 'react-i18next'

import { useSubscribeReservationToNewsletter } from '@/hooks/api/use-reservation-checkout'
import NewsletterEnvelopeImage from '@/images/subscription-illustration.webp'

export default function CheckoutSuccessNewsletterBanner({
    locale,
    onSubscribed,
}: {
    locale: string
    onSubscribed?: () => void
}) {
    const { t } = useTranslation()
    const subscription = useSubscribeReservationToNewsletter(onSubscribed)
    const subscribed = subscription.data?.subscribed === true
    const failed = subscription.isError
        || (subscription.isSuccess && subscription.data.subscribed !== true)

    return (
        <Box
            data-testid="checkout-success-newsletter-banner"
            sx={{
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                backgroundColor: 'common.white',
                border: '1px solid rgba(15, 23, 42, 0.08)',
                borderRadius: '10px',
                boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
                px: {
                    xs: 3,
                    sm: 4,
                },
                py: {
                    xs: 3,
                    sm: 4,
                },
                textAlign: 'center',
            }}
        >
            <Box
                component="img"
                src={NewsletterEnvelopeImage}
                alt=""
                aria-hidden="true"
                data-testid="newsletter-background-icon"
                sx={{
                    position: 'absolute',
                    right: {
                        xs: 10,
                        sm: 16,
                    },
                    bottom: {
                        xs: 10,
                        sm: 16,
                    },
                    width: {
                        xs: 84,
                        sm: 144,
                    },
                    height: 'auto',
                    opacity: {
                        xs: 0.42,
                        sm: 0.72,
                    },
                    transform: 'rotate(-6deg)',
                    pointerEvents: 'none',
                    userSelect: 'none',
                }}
            />

            <Typography
                component="h2"
                level="h3"
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    color: '#111827',
                    fontWeight: 600,
                    fontSize: {
                        xs: '1.25rem',
                        md: '1.5rem',
                    },
                    lineHeight: 1.2,
                }}
            >
                {t('success.newsletterBanner.title')}
            </Typography>

            {subscribed ? (
                <Typography
                    role="status"
                    aria-live="polite"
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        color: '#4B5563',
                        fontWeight: 500,
                        maxWidth: 520,
                    }}
                >
                    {t('success.newsletterBanner.success')}
                </Typography>
            ) : (
                <>
                    <Button
                        loading={subscription.isPending}
                        disabled={subscription.isPending}
                        onClick={() => subscription.mutate({ locale })}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            minWidth: 190,
                            borderRadius: '10px',
                            backgroundColor: '#111827',
                            fontSize: '15px',
                            padding: '0.75rem 1.25rem',
                            '&:hover': {
                                backgroundColor: '#1F2937',
                            },
                        }}
                    >
                        {t('success.newsletterBanner.button')}
                    </Button>
                    {failed ? (
                        <Typography
                            role="alert"
                            sx={{
                                position: 'relative',
                                zIndex: 1,
                                color: 'danger.600',
                                fontSize: '0.875rem',
                                maxWidth: 520,
                            }}
                        >
                            {t('success.newsletterBanner.error')}
                        </Typography>
                    ) : null}
                </>
            )}
        </Box>
    )
}
